/**
 * RTP Analytics page — injury distribution charts + PDF export.
 *
 * Reads from:
 *   GET /api/rtp/analytics/injury-distribution (filters: team, athlete, type, season, date_from, date_to)
 *   POST /api/rtp/analytics/export-pdf
 */
import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import { toast } from 'sonner';
import {
  PieChart, Pie, Cell, ResponsiveContainer, Tooltip,
  BarChart, Bar, XAxis, YAxis, CartesianGrid,
  LineChart, Line,
} from 'recharts';
import {
  ArrowLeft, AlertTriangle, Activity, Filter, Download, Loader2,
} from 'lucide-react';

const INJURY_TYPES = [
  'Hamstring', 'Quadriceps', 'Adductor / Groin', 'Calf',
  'Knee Ligament', 'Meniscus', 'Ankle Sprain', 'Shoulder',
  'Back / Lumbar', 'Muscle Fatigue / Overload', 'Concussion',
  'Tendinopathy', 'Fracture', 'Illness', 'Other',
];

const COLORS = [
  '#f59e0b', '#ef4444', '#3b82f6', '#10b981', '#8b5cf6',
  '#ec4899', '#06b6d4', '#f97316', '#84cc16', '#a855f7',
  '#0ea5e9', '#f43f5e', '#22d3ee', '#eab308', '#64748b',
];

export default function RTPAnalyticsPage() {
  const { api, user } = useAuth();
  const navigate = useNavigate();

  const [filters, setFilters] = useState({
    team_id: '',
    athlete_id: '',
    injury_type: '',
    season: '',
    date_from: '',
    date_to: '',
  });
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [teams, setTeams] = useState([]);
  const [athletes, setAthletes] = useState([]);

  const fetchAnalytics = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      Object.entries(filters).forEach(([k, v]) => {
        if (v !== '' && v != null) params[k] = v;
      });
      const r = await api.get('/rtp/analytics/injury-distribution', { params });
      setData(r.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to load analytics');
    } finally {
      setLoading(false);
    }
  }, [api, filters]);

  useEffect(() => {
    api.get('/teams').then(r => setTeams(r.data || [])).catch(() => {});
    api.get('/athletes').then(r => setAthletes(r.data || [])).catch(() => {});
  }, [api]);

  useEffect(() => { fetchAnalytics(); }, [fetchAnalytics]);

  const handleExport = async () => {
    setExporting(true);
    try {
      const payload = {
        organization_name: user?.organization_name || 'FB Performance',
      };
      Object.entries(filters).forEach(([k, v]) => {
        if (v !== '' && v != null) payload[k] = k === 'season' ? Number(v) : v;
      });
      const res = await api.post('/rtp/analytics/export-pdf', payload, { responseType: 'blob' });
      const url = URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = 'rtp_injury_distribution.pdf';
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success('PDF downloaded');
    } catch {
      toast.error('Failed to export PDF');
    }
    setExporting(false);
  };

  const resetFilters = () => setFilters({
    team_id: '', athlete_id: '', injury_type: '', season: '', date_from: '', date_to: '',
  });

  return (
    <div className="min-h-screen bg-zinc-950 p-4 sm:p-6">
      <div className="max-w-7xl mx-auto space-y-6">
        {/* Header */}
        <div className="flex items-center gap-3">
          <Button
            variant="ghost" size="sm"
            onClick={() => navigate('/staff/reports')}
            className="text-zinc-400 hover:text-zinc-100"
            data-testid="rtp-analytics-back"
          >
            <ArrowLeft className="w-4 h-4 mr-1" /> Back to Reports
          </Button>
        </div>
        <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3">
          <div>
            <h1 className="text-3xl font-bold text-zinc-100 flex items-center gap-2">
              <AlertTriangle className="w-7 h-7 text-amber-400" />
              RTP Analytics
            </h1>
            <p className="text-zinc-500 text-sm mt-1">Injury distribution, trends and team-level insights.</p>
          </div>
          <Button
            onClick={handleExport}
            disabled={exporting || !data || data.total_injuries === 0}
            className="bg-amber-500 hover:bg-amber-400 text-zinc-950"
            data-testid="rtp-analytics-export-pdf"
          >
            {exporting ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Download className="w-4 h-4 mr-1" />}
            Export PDF
          </Button>
        </div>

        {/* Filters */}
        <Card className="bg-zinc-900/50 border-zinc-800" data-testid="rtp-analytics-filters">
          <CardHeader className="pb-3">
            <CardTitle className="text-sm text-zinc-400 flex items-center gap-2">
              <Filter className="w-4 h-4" /> Filters
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
              <div>
                <Label className="text-xs text-zinc-500">Team</Label>
                <Select value={filters.team_id || 'ALL'} onValueChange={v => setFilters(f => ({ ...f, team_id: v === 'ALL' ? '' : v }))}>
                  <SelectTrigger className="bg-zinc-950 border-zinc-800 text-sm" data-testid="filter-team"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-zinc-900 border-zinc-800">
                    <SelectItem value="ALL">All teams</SelectItem>
                    {teams.map(t => <SelectItem key={t.id} value={t.id}>{t.code || t.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs text-zinc-500">Athlete</Label>
                <Select value={filters.athlete_id || 'ALL'} onValueChange={v => setFilters(f => ({ ...f, athlete_id: v === 'ALL' ? '' : v }))}>
                  <SelectTrigger className="bg-zinc-950 border-zinc-800 text-sm" data-testid="filter-athlete"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-zinc-900 border-zinc-800 max-h-72">
                    <SelectItem value="ALL">All athletes</SelectItem>
                    {athletes.map(a => <SelectItem key={a.id} value={a.id}>{a.first_name} {a.last_name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs text-zinc-500">Injury Type</Label>
                <Select value={filters.injury_type || 'ALL'} onValueChange={v => setFilters(f => ({ ...f, injury_type: v === 'ALL' ? '' : v }))}>
                  <SelectTrigger className="bg-zinc-950 border-zinc-800 text-sm" data-testid="filter-injury-type"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-zinc-900 border-zinc-800 max-h-72">
                    <SelectItem value="ALL">All types</SelectItem>
                    {INJURY_TYPES.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs text-zinc-500">Season (start year)</Label>
                <Input
                  type="number" min="2000" max="2099"
                  placeholder="e.g. 2024"
                  value={filters.season}
                  onChange={e => setFilters(f => ({ ...f, season: e.target.value }))}
                  className="bg-zinc-950 border-zinc-800 text-sm"
                  data-testid="filter-season"
                />
              </div>
              <div>
                <Label className="text-xs text-zinc-500">From</Label>
                <Input
                  type="date" value={filters.date_from}
                  onChange={e => setFilters(f => ({ ...f, date_from: e.target.value }))}
                  className="bg-zinc-950 border-zinc-800 text-sm"
                  data-testid="filter-date-from"
                />
              </div>
              <div>
                <Label className="text-xs text-zinc-500">To</Label>
                <Input
                  type="date" value={filters.date_to}
                  onChange={e => setFilters(f => ({ ...f, date_to: e.target.value }))}
                  className="bg-zinc-950 border-zinc-800 text-sm"
                  data-testid="filter-date-to"
                />
              </div>
            </div>
            <div className="flex justify-end mt-3">
              <Button variant="ghost" size="sm" onClick={resetFilters} className="text-zinc-400 hover:text-zinc-200" data-testid="reset-filters">
                Reset filters
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Summary cards */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <SummaryCard label="Total Injuries" value={data?.total_injuries ?? 0} icon={Activity} accent="amber" testid="kpi-total" />
          <SummaryCard label="Active Cases" value={data?.active_cases ?? 0} icon={AlertTriangle} accent="rose" testid="kpi-active" />
          <SummaryCard label="Distinct Types" value={data?.by_type?.length ?? 0} icon={Filter} accent="blue" testid="kpi-types" />
          <SummaryCard label="Teams Affected" value={data?.by_team?.length ?? 0} icon={Activity} accent="emerald" testid="kpi-teams" />
        </div>

        {/* Charts */}
        {loading ? (
          <div className="flex items-center justify-center py-20 text-zinc-500">
            <Loader2 className="w-5 h-5 animate-spin mr-2" /> Loading analytics…
          </div>
        ) : !data || data.total_injuries === 0 ? (
          <Card className="bg-zinc-900/50 border-zinc-800">
            <CardContent className="py-16 text-center text-zinc-500" data-testid="empty-state">
              <Activity className="w-10 h-10 mx-auto mb-3 opacity-50" />
              No injuries match the current filters.
            </CardContent>
          </Card>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {/* Pie: by type */}
            <Card className="bg-zinc-900/50 border-zinc-800">
              <CardHeader><CardTitle className="text-sm text-zinc-400">Distribution by Injury Type</CardTitle></CardHeader>
              <CardContent>
                <div className="h-80" data-testid="chart-by-type-pie">
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie
                        data={data.by_type}
                        dataKey="count" nameKey="type"
                        cx="50%" cy="50%" outerRadius={100} innerRadius={50}
                        label={(e) => `${e.type} (${e.percentage}%)`}
                        labelLine={false}
                      >
                        {data.by_type.map((entry, i) => (
                          <Cell key={entry.type} fill={COLORS[i % COLORS.length]} />
                        ))}
                      </Pie>
                      <Tooltip
                        formatter={(value, name) => [value, name]}
                        contentStyle={{ background: '#18181b', border: '1px solid #3f3f46', color: '#fff' }}
                      />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>

            {/* Bar: by team */}
            <Card className="bg-zinc-900/50 border-zinc-800">
              <CardHeader><CardTitle className="text-sm text-zinc-400">Injuries by Team</CardTitle></CardHeader>
              <CardContent>
                <div className="h-80" data-testid="chart-by-team-bar">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={data.by_team.map(t => ({ name: t.team_code || t.team_name || t.team_id.slice(0, 6), count: t.count }))}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#3f3f46" />
                      <XAxis dataKey="name" tick={{ fill: '#a1a1aa', fontSize: 11 }} />
                      <YAxis tick={{ fill: '#a1a1aa', fontSize: 11 }} allowDecimals={false} />
                      <Tooltip contentStyle={{ background: '#18181b', border: '1px solid #3f3f46', color: '#fff' }} />
                      <Bar dataKey="count" fill="#f59e0b" radius={[4, 4, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>

            {/* Line: by month */}
            <Card className="bg-zinc-900/50 border-zinc-800 lg:col-span-2">
              <CardHeader><CardTitle className="text-sm text-zinc-400">Monthly Trend</CardTitle></CardHeader>
              <CardContent>
                <div className="h-72" data-testid="chart-by-month-line">
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart data={data.by_month}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#3f3f46" />
                      <XAxis dataKey="month" tick={{ fill: '#a1a1aa', fontSize: 11 }} />
                      <YAxis tick={{ fill: '#a1a1aa', fontSize: 11 }} allowDecimals={false} />
                      <Tooltip contentStyle={{ background: '#18181b', border: '1px solid #3f3f46', color: '#fff' }} />
                      <Line type="monotone" dataKey="count" stroke="#f59e0b" strokeWidth={2} dot={{ fill: '#f59e0b', r: 3 }} />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>

            {/* Breakdown table */}
            <Card className="bg-zinc-900/50 border-zinc-800 lg:col-span-2">
              <CardHeader><CardTitle className="text-sm text-zinc-400">Full Breakdown</CardTitle></CardHeader>
              <CardContent>
                <div className="overflow-x-auto" data-testid="breakdown-table">
                  <table className="w-full text-sm">
                    <thead className="text-zinc-500 text-xs">
                      <tr className="border-b border-zinc-800">
                        <th className="text-left py-2 px-3">Injury Type</th>
                        <th className="text-right py-2 px-3">Count</th>
                        <th className="text-right py-2 px-3">Percentage</th>
                      </tr>
                    </thead>
                    <tbody>
                      {data.by_type.map((row, i) => (
                        <tr key={row.type} className="border-b border-zinc-800/50">
                          <td className="py-2 px-3">
                            <span className="inline-flex items-center gap-2">
                              <span className="w-2.5 h-2.5 rounded-full inline-block" style={{ background: COLORS[i % COLORS.length] }} />
                              {row.type}
                            </span>
                          </td>
                          <td className="py-2 px-3 text-right text-zinc-200">{row.count}</td>
                          <td className="py-2 px-3 text-right text-zinc-400">{row.percentage}%</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
          </div>
        )}
      </div>
    </div>
  );
}

function SummaryCard({ label, value, icon: Icon, accent, testid }) {
  const colorMap = {
    amber: 'text-amber-400 bg-amber-500/10',
    rose: 'text-rose-400 bg-rose-500/10',
    blue: 'text-blue-400 bg-blue-500/10',
    emerald: 'text-emerald-400 bg-emerald-500/10',
  };
  return (
    <Card className="bg-zinc-900/50 border-zinc-800" data-testid={testid}>
      <CardContent className="p-4 flex items-center justify-between">
        <div>
          <p className="text-zinc-400 text-xs uppercase tracking-wider">{label}</p>
          <p className="text-3xl font-bold text-zinc-100 mt-1">{value}</p>
        </div>
        <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${colorMap[accent]}`}>
          <Icon className="w-5 h-5" />
        </div>
      </CardContent>
    </Card>
  );
}
